import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { fetchServiceProfiles } from '@/services/serviceProfiles';
import { createRedesign } from '@/services/redesigns';
import type { RedesignMode, ServiceProfile } from '@/types';

type WizardStep = 'choose' | 'details' | 'confirm';

const USE_CASE_CARDS: { mode: RedesignMode; title: string; description: string; icon: string }[] = [
  { mode: 'redesign', title: 'Redesign existing UI', description: 'Capture screenshots of your live app and get AI-generated redesigns', icon: '\uD83C\uDFA8' },
  { mode: 'feature', title: 'Design a new feature', description: 'Describe a feature; get designs that match your existing app\'s style', icon: '\u2728' },
  { mode: 'greenfield', title: 'Build from scratch', description: 'No existing UI. Describe your app and get a complete design', icon: '\uD83D\uDE80' },
];

export function NewProjectWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState<WizardStep>('choose');
  const [mode, setMode] = useState<RedesignMode>('redesign');
  const [name, setName] = useState('');
  const [serviceProfileId, setServiceProfileId] = useState('');
  const [authStrategy, setAuthStrategy] = useState('');
  const [referenceRoutes, setReferenceRoutes] = useState('');
  const [startPath, setStartPath] = useState('');
  const [pathPrefix, setPathPrefix] = useState('');

  const { data: services } = useQuery({
    queryKey: ['service-profiles'],
    queryFn: fetchServiceProfiles,
  });

  const selectedService = services?.find((s: ServiceProfile) => s._id === serviceProfileId);

  const createMutation = useMutation({
    mutationFn: () => createRedesign(
      serviceProfileId,
      {
        name: name || undefined,
        authStrategy: authStrategy || undefined,
        mode,
        referenceRoutes: referenceRoutes ? referenceRoutes.split('\n').map(r => r.trim()).filter(Boolean) : undefined,
        startPath: startPath || undefined,
        pathPrefix: pathPrefix || undefined,
      },
    ),
    onSuccess: (data) => {
      const result = data as { redesignId?: string };
      if (result.redesignId) {
        navigate(`/ui-orchestrator/projects/redesign-${result.redesignId}`);
      } else {
        navigate('/ui-orchestrator/');
      }
    },
  });

  const handleChoose = (chosen: RedesignMode) => {
    setMode(chosen);
    setStep('details');
  };

  const handleBack = () => {
    if (step === 'confirm') setStep('details');
    else if (step === 'details') setStep('choose');
    else navigate('/ui-orchestrator/');
  };

  const canProceedToConfirm = () => {
    if (!name.trim()) return false;
    if (mode === 'redesign' || mode === 'feature') return !!serviceProfileId;
    return true; // greenfield: service is optional
  };

  return (
    <div style={{ maxWidth: '680px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
        <button onClick={handleBack} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', color: 'var(--text-muted)' }}>
          &larr; Back
        </button>
        <h1 style={{ fontSize: '18px', fontWeight: 700, margin: 0 }}>New Project</h1>
      </div>

      {/* Step 1: Choose use case */}
      {step === 'choose' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginBottom: '8px' }}>What do you want to do?</p>
          {USE_CASE_CARDS.map((card) => (
            <div
              key={card.mode}
              onClick={() => handleChoose(card.mode)}
              style={{
                padding: '20px',
                border: '1px solid var(--color-border)',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'border-color 150ms, box-shadow 150ms',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--color-primary)'; e.currentTarget.style.boxShadow = '0 0 0 1px var(--color-primary)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--color-border)'; e.currentTarget.style.boxShadow = 'none'; }}
            >
              <div style={{ fontSize: '20px', marginBottom: '8px' }}>{card.icon}</div>
              <div style={{ fontSize: '15px', fontWeight: 600, marginBottom: '4px' }}>{card.title}</div>
              <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>{card.description}</div>
            </div>
          ))}
        </div>
      )}

      {/* Step 2: Details */}
      {step === 'details' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <p style={{ color: 'var(--text-muted)', fontSize: '14px', margin: 0 }}>
            {mode === 'redesign' && 'Configure your redesign project'}
            {mode === 'feature' && 'Configure the new feature project'}
            {mode === 'greenfield' && 'Configure your new app project'}
          </p>

          {/* Project name */}
          <label style={{ display: 'block' }}>
            <span style={{ fontSize: '13px', fontWeight: 500 }}>Project Name</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="My Redesign Project"
              style={{
                display: 'block', width: '100%', padding: '8px 12px', marginTop: '4px',
                borderRadius: '6px', border: '1px solid var(--color-border)', fontSize: '13px',
                boxSizing: 'border-box',
              }}
            />
          </label>

          {/* Service selector (required for redesign/feature, optional for greenfield) */}
          <label style={{ display: 'block' }}>
            <span style={{ fontSize: '13px', fontWeight: 500 }}>
              Service {mode === 'greenfield' ? '(optional — for style reference)' : ''}
            </span>
            <select
              value={serviceProfileId}
              onChange={(e) => { setServiceProfileId(e.target.value); setAuthStrategy(''); }}
              style={{ display: 'block', width: '100%', padding: '8px 12px', marginTop: '4px', borderRadius: '6px', border: '1px solid var(--color-border)', fontSize: '13px' }}
            >
              <option value="">Select a service...</option>
              {(services ?? []).map((s: ServiceProfile) => (
                <option key={s._id} value={s._id}>{s.displayName} ({s.repo})</option>
              ))}
            </select>
          </label>

          {/* Auth strategy */}
          {selectedService && selectedService.authStrategies.length > 0 && (
            <label style={{ display: 'block' }}>
              <span style={{ fontSize: '13px', fontWeight: 500 }}>Auth Strategy</span>
              <select
                value={authStrategy}
                onChange={(e) => setAuthStrategy(e.target.value)}
                style={{ display: 'block', width: '100%', padding: '8px 12px', marginTop: '4px', borderRadius: '6px', border: '1px solid var(--color-border)', fontSize: '13px' }}
              >
                <option value="">Default auth</option>
                {selectedService.authStrategies.map((s) => (
                  <option key={s.name} value={s.name}>{s.name} — {s.description}</option>
                ))}
              </select>
            </label>
          )}

          {/* Scoped crawl (redesign mode only) */}
          {mode === 'redesign' && (
            <>
              <label style={{ display: 'block' }}>
                <span style={{ fontSize: '13px', fontWeight: 500 }}>Start Path (optional)</span>
                <span style={{ fontSize: '11px', color: 'var(--text-muted)', marginLeft: '8px' }}>Path to start crawling from. Defaults to /</span>
                <input
                  type="text"
                  value={startPath}
                  onChange={(e) => setStartPath(e.target.value)}
                  placeholder="/"
                  style={{
                    display: 'block', width: '100%', padding: '8px 12px', marginTop: '4px',
                    borderRadius: '6px', border: '1px solid var(--color-border)', fontSize: '13px',
                    fontFamily: 'monospace', boxSizing: 'border-box',
                  }}
                />
              </label>
              <label style={{ display: 'block' }}>
                <span style={{ fontSize: '13px', fontWeight: 500 }}>Path Filter (optional)</span>
                <span style={{ fontSize: '11px', color: 'var(--text-muted)', marginLeft: '8px' }}>Only capture pages under this path prefix</span>
                <input
                  type="text"
                  value={pathPrefix}
                  onChange={(e) => setPathPrefix(e.target.value)}
                  placeholder="/admin"
                  style={{
                    display: 'block', width: '100%', padding: '8px 12px', marginTop: '4px',
                    borderRadius: '6px', border: '1px solid var(--color-border)', fontSize: '13px',
                    fontFamily: 'monospace', boxSizing: 'border-box',
                  }}
                />
              </label>
            </>
          )}

          {/* Reference routes (feature mode only) */}
          {mode === 'feature' && (
            <label style={{ display: 'block' }}>
              <span style={{ fontSize: '13px', fontWeight: 500 }}>Reference Routes (optional, one per line)</span>
              <span style={{ fontSize: '11px', color: 'var(--text-muted)', marginLeft: '8px' }}>Routes to capture for style reference</span>
              <textarea
                value={referenceRoutes}
                onChange={(e) => setReferenceRoutes(e.target.value)}
                rows={3}
                placeholder={'/dashboard\n/settings'}
                style={{
                  display: 'block', width: '100%', padding: '8px', marginTop: '4px',
                  borderRadius: '6px', border: '1px solid var(--color-border)', fontFamily: 'monospace',
                  fontSize: '13px', resize: 'vertical', boxSizing: 'border-box',
                }}
              />
            </label>
          )}

          <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
            <button
              onClick={() => setStep('confirm')}
              disabled={!canProceedToConfirm()}
              style={{
                padding: '8px 20px', borderRadius: '6px', border: 'none',
                backgroundColor: canProceedToConfirm() ? 'var(--color-primary)' : 'var(--color-border)',
                color: '#fff', fontSize: '13px', fontWeight: 600,
                cursor: canProceedToConfirm() ? 'pointer' : 'not-allowed',
              }}
            >
              Review
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Confirm */}
      {step === 'confirm' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={{ padding: '16px', border: '1px solid var(--color-border)', borderRadius: '8px', background: 'var(--bg-secondary)' }}>
            {name && (
              <>
                <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '4px' }}>Project Name</div>
                <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px' }}>{name}</div>
              </>
            )}
            <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '4px' }}>Type</div>
            <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px' }}>
              {mode === 'redesign' && 'Redesign existing UI'}
              {mode === 'feature' && 'Design a new feature'}
              {mode === 'greenfield' && 'Build from scratch'}
            </div>

            {selectedService && (
              <>
                <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '4px' }}>Service</div>
                <div style={{ fontSize: '14px', marginBottom: '12px' }}>{selectedService.displayName} ({selectedService.repo})</div>
              </>
            )}

            {authStrategy && (
              <>
                <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '4px' }}>Auth Strategy</div>
                <div style={{ fontSize: '14px', marginBottom: '12px' }}>{authStrategy}</div>
              </>
            )}

            {startPath && (
              <>
                <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '4px' }}>Start Path</div>
                <div style={{ fontSize: '14px', fontFamily: 'monospace', marginBottom: '12px' }}>{startPath}</div>
              </>
            )}

            {pathPrefix && (
              <>
                <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '4px' }}>Path Filter</div>
                <div style={{ fontSize: '14px', fontFamily: 'monospace', marginBottom: '12px' }}>{pathPrefix}</div>
              </>
            )}

            {mode === 'feature' && referenceRoutes && (
              <>
                <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '4px' }}>Reference Routes</div>
                <div style={{ fontSize: '13px', fontFamily: 'monospace' }}>{referenceRoutes}</div>
              </>
            )}
          </div>

          {createMutation.isError && (
            <div style={{ padding: '12px', background: '#fee2e2', borderRadius: '8px', color: 'var(--color-error)', fontSize: '13px' }}>
              {(createMutation.error as Error).message}
            </div>
          )}

          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => createMutation.mutate()}
              disabled={createMutation.isPending}
              style={{
                padding: '8px 20px', borderRadius: '6px', border: 'none',
                backgroundColor: createMutation.isPending ? 'var(--bg-disabled)' : 'var(--color-success)',
                color: '#fff', fontSize: '13px', fontWeight: 600,
                cursor: createMutation.isPending ? 'not-allowed' : 'pointer',
              }}
            >
              {createMutation.isPending ? 'Creating...' : 'Create Project'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
